import React, { useCallback, useEffect, useState } from "react";
import { api, CATEGORY_LABELS, FBC_STATUS_LABELS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, Plus, ShieldCheck, Trash2, IndianRupee, Skull } from "lucide-react";
import { toast } from "sonner";

const empty = {
  full_name: "", phone: "", whatsapp_number: "", email: "", age: "",
  category: "bk_student", insurance_eligible: false, pin_code: "",
  city: "", state: "", locality: "", zone: "", address: "", gender: "", notes: "",
};

export default function Members() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [fbcFilter, setFbcFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (fbcFilter !== "all") params.fbc_status = fbcFilter;
    if (categoryFilter !== "all") params.category = categoryFilter;
    const r = await api.get("/members", { params });
    setMembers(r.data);
  }, [q, fbcFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/members/${toDelete.id}`);
      toast.success("Member deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const togglePayment = async (m) => {
    const nextPaid = m.payment_status !== "paid";
    try {
      await api.post(`/members/${m.id}/mark-paid`, { paid: nextPaid });
      toast.success(nextPaid ? "₹1499 marked as received" : "Payment mark cleared");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update payment");
    }
  };

  const submit = async () => {
    if (!form.full_name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      await api.post("/members", form);
      toast.success("Member added");
      setForm(empty); setOpen(false); load();
    } catch (e) {
      toast.error("Failed to add member");
    }
  };

  const wipeTestData = async () => {
    if (!window.confirm("This will DELETE all orders, enrollments and WhatsApp session data. Type WIPE in the next prompt to confirm.")) return;
    const conf = window.prompt('Type WIPE to confirm test-data purge:');
    if (conf !== "WIPE") { toast.error("Cancelled"); return; }
    try {
      const r = await api.post("/admin/wipe-test-data", { confirm: "WIPE" });
      toast.success(`Wiped: ${Object.entries(r.data?.wiped || {}).map(([k,v]) => k+':'+v).join(', ')}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed (super-admin only)");
    }
  };

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="label-caps text-accent mb-2">CRM</div>
          <h1 className="font-heading text-4xl tracking-tight text-primary">Members</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Enrolled Medishiv members. FBC pricing varies by category — free for Insured (BK Health Safety Program), ₹1199 for teachers without cover, ₹1499 for students.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="wipe-test-data-btn" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={wipeTestData}>
            <Skull size={14} /> Wipe Test Data
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-member-btn" className="bg-primary hover:bg-primary/90 rounded-full">
              <Plus size={16} className="mr-1.5" /> Add member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">New member</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div className="col-span-2"><Label>Full name</Label><Input data-testid="member-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
              <div><Label>Phone</Label><Input data-testid="member-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>PIN code</Label><Input data-testid="member-pin-input" value={form.pin_code} onChange={(e) => setForm({ ...form, pin_code: e.target.value })} /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="member-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="col-span-2 flex items-center gap-3">
                <Switch data-testid="member-insurance-switch" checked={form.insurance_eligible} onCheckedChange={(v) => setForm({ ...form, insurance_eligible: v })} />
                <span className="text-sm">Insurance eligible (Brahma Kumaris teacher program)</span>
              </div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter className="mt-4">
              <Button data-testid="save-member-btn" onClick={submit} className="bg-primary hover:bg-primary/90">Save member</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <Card className="p-4 border-border">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input data-testid="member-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone, email, PIN, or city…" className="pl-10 h-11 rounded-lg" />
          </div>
          <Select value={fbcFilter} onValueChange={setFbcFilter}>
            <SelectTrigger data-testid="member-fbc-filter" className="w-full md:w-56 h-11"><SelectValue placeholder="FBC status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All FBC statuses</SelectItem>
              {Object.entries(FBC_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="member-category-filter" className="w-full md:w-56 h-11"><SelectValue placeholder="Category"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Insurance</TableHead>
              <TableHead>PIN / City</TableHead>
              <TableHead>FBC Status</TableHead>
              <TableHead>Membership ₹1499</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No members found</TableCell></TableRow>}
            {members.map((m) => (
              <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                <TableCell>
                  <div className="font-medium text-foreground">{m.full_name}</div>
                  <div className="text-xs font-mono text-primary" data-testid={`member-code-${m.id}`}>{m.membership_id || m.id}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{m.phone}</div>
                  <div className="text-xs text-muted-foreground">{m.email}</div>
                </TableCell>
                <TableCell><Badge variant="secondary" className="bg-secondary text-primary">{CATEGORY_LABELS[m.category] || m.category}</Badge></TableCell>
                <TableCell>{m.insurance_eligible ? <span className="inline-flex items-center gap-1 text-primary text-sm"><ShieldCheck size={14}/> Yes</span> : <span className="text-muted-foreground text-sm">—</span>}</TableCell>
                <TableCell>
                  <div className="font-mono text-sm">{m.pin_code}</div>
                  {m.city && <div className="text-xs text-muted-foreground">{m.city}</div>}
                </TableCell>
                <TableCell>
                  <Badge data-testid={`member-fbc-status-${m.id}`} variant="secondary" className="bg-emerald-50 text-emerald-800">
                    {FBC_STATUS_LABELS[m.fbc_status] || FBC_STATUS_LABELS.not_availed}
                  </Badge>
                </TableCell>
                <TableCell>
                  {m.category === "bk_student" ? (
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid={`member-payment-switch-${m.id}`}
                        checked={m.payment_status === "paid"}
                        onCheckedChange={() => togglePayment(m)}
                      />
                      <Badge
                        data-testid={`member-payment-badge-${m.id}`}
                        variant="secondary"
                        className={m.payment_status === "paid"
                          ? "bg-emerald-50 text-emerald-800"
                          : "bg-amber-50 text-amber-800"}
                      >
                        <IndianRupee size={11} className="mr-0.5" />
                        {m.payment_status === "paid" ? "Received" : "Pending"}
                      </Badge>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">Not applicable</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{new Date(m.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={`member-delete-${m.id}`}
                    size="icon"
                    variant="ghost"
                    className="text-destructive hover:bg-red-50"
                    onClick={() => setToDelete(m)}
                  >
                    <Trash2 size={16}/>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="member-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this member?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && (<>Member <span className="font-medium">{toDelete.full_name}</span> ({toDelete.phone}) will be soft-deleted. Historic data is kept for audit and can be restored.</>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="member-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="member-delete-confirm-btn" onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
